/**
 * Skinned Base UI NumberField: segmented [-] value [+] stepper that reuses Input's
 * container classes (outlined/minimal/filled) so it drops into InputField unchanged.
 */
'use client'

import { NumberField } from '@base-ui/react/number-field'
import { cn } from '../lib/utils'
import { MinusIcon, PlusIcon } from './icon'
import {
  inputContainerClass,
  inputMinimalClass,
  inputFilledClass,
} from './input'

type NumberInputProps = NumberField.Root.Props & {
  /** Bare stepper: no border/background/padding/radius, compact inline layout. */
  minimal?: boolean
  /** Tinted-background variant matching Input's `filled` look. */
  filled?: boolean
  className?: string
  placeholder?: string
}

function NumberInput({
  minimal,
  filled,
  className,
  placeholder,
  ...rootProps
}: NumberInputProps) {
  const containerClass = minimal
    ? inputMinimalClass
    : filled
      ? inputFilledClass
      : inputContainerClass

  const segmentClass = minimal
    ? 'flex shrink-0 cursor-pointer items-center justify-center select-none p-1 disabled:cursor-not-allowed disabled:opacity-40'
    : 'flex w-14 shrink-0 cursor-pointer items-center justify-center select-none disabled:cursor-not-allowed disabled:opacity-40'

  return (
    <NumberField.Root {...rootProps}>
      <NumberField.Group
        className={cn(
          containerClass,
          'items-stretch overflow-hidden px-0',
          className,
        )}
      >
        <NumberField.Decrement
          className={cn(segmentClass, !minimal && 'border-outline border-e')}
        >
          <MinusIcon className="size-5" />
        </NumberField.Decrement>
        <NumberField.Input
          placeholder={placeholder}
          className="placeholder:text-on-surface-secondary w-full text-center outline-none placeholder:select-none disabled:cursor-not-allowed"
        />
        <NumberField.Increment
          className={cn(segmentClass, !minimal && 'border-outline border-s')}
        >
          <PlusIcon className="size-5" />
        </NumberField.Increment>
      </NumberField.Group>
    </NumberField.Root>
  )
}

export { NumberInput, type NumberInputProps }
